import { useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Presentation, BookOpen, Loader2, Lock } from 'lucide-react';

const documents = [
  {
    id: 'buyer',
    title: 'Beslutsunderlag — för inköp och upphandling',
    description: 'Beslutsunderlag 5.0 för skolledning, förvaltning, IT, dataskydd och upphandling. Omfattar problembild, plattformens roller och funktioner, integritet, pilot med budget, införande/utbildning, vägen till beslut samt tekniska bilagor.',
    endpoint: '/presentation/buyer-pdf',
    filename: 'vapelarm_beslutsunderlag.pdf',
    icon: FileText,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    pages: '12 sidor',
    format: 'Landscape A4',
    roles: ['superadmin'],
  },
  {
    id: 'presentation',
    title: 'Systempresentation — Vapelarm Cloud',
    description: '11-sidig presentation av systemet: problemet, AI-teknik, sensorn, plattformens funktioner, de tre rollerna, integritet/GDPR, resultat och företaget.',
    endpoint: '/presentation/pdf',
    filename: 'vapelarm_presentation.pdf',
    icon: Presentation,
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    pages: '11 sidor',
    format: 'Landscape A4',
    roles: ['superadmin', 'school_admin'],
  },
  {
    id: 'personalguide',
    title: 'Personalguide',
    description: 'Komplett guide för skolpersonal — snabbreferens, steg-för-steg, feedback-förklaring, ärendehantering, FAQ och integritet.',
    endpoint: '/onboarding/pdf',
    filename: 'vapelarm_personalguide.pdf',
    icon: BookOpen,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    pages: '5 sidor',
    format: 'A4 stående',
    roles: ['superadmin', 'school_admin', 'staff'],
  },
];

const roleLabels = {
  superadmin: 'Superadmin',
  school_admin: 'Skoladmin',
  staff: 'Personal',
};

export default function DocumentsPage() {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);

  const userRole = user?.role || 'staff';
  const visibleDocs = documents.filter(d => d.roles.includes(userRole));

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await api.get(doc.endpoint, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${doc.title} nedladdad`);
    } catch {
      toast.error('Kunde inte ladda ner dokumentet');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="documents-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
          Dokument
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm">
          Ladda ner presentationer och guider som PDF
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {visibleDocs.map((doc) => {
          const Icon = doc.icon;
          const isLoading = downloading === doc.id;
          return (
            <Card
              key={doc.id}
              className={`${doc.border} hover:shadow-md transition-shadow`}
              data-testid={`doc-card-${doc.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg ${doc.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`h-6 w-6 ${doc.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-chivo font-bold text-lg text-navy dark:text-white">
                      {doc.title}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-ibm">
                      {doc.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className="text-xs font-ibm text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                        PDF
                      </span>
                      <span className="text-xs font-ibm text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                        {doc.pages}
                      </span>
                      <span className="text-xs font-ibm text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                        {doc.format}
                      </span>
                      {userRole === 'superadmin' && (
                        <span className="text-[10px] font-ibm text-slate-400 flex items-center gap-1 ml-1">
                          <Lock className="h-3 w-3" />
                          {doc.roles.map(r => roleLabels[r]).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => handleDownload(doc)}
                    disabled={isLoading}
                    className="gap-2"
                    data-testid={`download-${doc.id}-btn`}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Ladda ner
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {visibleDocs.length === 0 && (
          <Card className="md:col-span-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-ibm">Inga dokument tillgängliga för din roll</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
